<script lang="ts">
	import { onDestroy, onMount } from 'svelte'
	import type { SupersetConfig } from '@repo/commons/models'
	import { getSupersetToken, initSuperset } from '@repo/commons/utils'

	type EmbeddedDashboardHandle = {
		unmount: () => void
	}

	export let appName: string

	let mountPoint: HTMLDivElement
	let supersetConfig: SupersetConfig | null = null
	let embeddedDashboard: EmbeddedDashboardHandle | null = null
	let errorMessage: string | null = null
	let isLoading: boolean = true

	const normalizeAppName = (value: string): string => {
		const trimmed: string = value.trim()
		const upper: string = trimmed.toUpperCase()
		const normalized: string = upper.replace(/[^A-Z0-9]/g, '_')

		// appNames are full capital letters in the envs
		return normalized.toUpperCase()
	}

	onMount(async (): Promise<void> => {
		isLoading = true
		errorMessage = null

		try {
			if (import.meta.env.SSR) return

			const normalizedAppName: string = normalizeAppName(appName)
			if (!normalizedAppName) {
				throw new Error('appName is empty')
			}

			supersetConfig = await initSuperset(appName)

			const sdk: typeof import('@superset-ui/embedded-sdk') =
				await import('@superset-ui/embedded-sdk')
			const handle: unknown = await sdk.embedDashboard({
				id: supersetConfig.supersetDashboardId,
				supersetDomain: supersetConfig.supersetFrontendUrl,
				mountPoint,
				fetchGuestToken: (): Promise<string> => getSupersetToken(appName),
				iframeSandboxExtras: ['allow-top-navigation', 'allow-same-origin', 'allow-scripts']
			})

			embeddedDashboard = handle as EmbeddedDashboardHandle

			/*
				We need this hack, see:
				- https://stackoverflow.com/questions/76893237/superset-iframe-always-fixed-to-300x150-cannot-be-changed
				- https://github.com/apache/superset/issues/23793
			*/
			const iframe: Element | null = mountPoint.querySelector('iframe')
			if (iframe instanceof HTMLIFrameElement) {
				iframe.style.width = '100%'
				iframe.style.height = '100%'
				iframe.style.border = 'none'
			}
		} catch (err: unknown) {
			const message: string =
				err instanceof Error ? err.message : typeof err === 'string' ? err : 'Unknown error'
			errorMessage = message
		} finally {
			isLoading = false
		}
	})

	onDestroy((): void => {
		if (embeddedDashboard) {
			embeddedDashboard.unmount()
			embeddedDashboard = null
		}
	})
</script>

<div class="page-wrapper flex flex-col overflow-auto">
	{#if errorMessage}
		<div class="border-b border-red-200 bg-red-50 p-3">
			<p class="text-sm font-semibold text-red-700">Superset embed failed</p>
			<pre class="mt-2 whitespace-pre-wrap break-words text-xs text-red-700">{errorMessage}</pre>
		</div>
	{/if}

	{#if isLoading}
		<div class="p-3">
			<p class="text-sm text-gray-600">Loading dashboard…</p>
		</div>
	{/if}

	<div bind:this={mountPoint} class="dashboard-wrapper flex-1 min-h-0 w-full" />
</div>

<style>
	/* Fallback to guarantee fullscreen even if Tailwind classes are not generated for this package */
	.page-wrapper {
		display: flex;
		flex-direction: column;
		height: 100vh;
		overflow: auto;
		margin: 0;
		padding: 0;
	}

	.dashboard-wrapper {
		flex: 1;
		min-height: 0;
		width: 100%;
	}
</style>
